import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import axios from 'axios'
import SearchInput from '../ui/SearchInput'
import StatusToggle from '../ui/StatusToggle'
import ActionButtons from '../ui/ActionButtons'
import { AddIcon } from '../ui/AddIcon'
import ConfirmModal from '../ui/ConfirmModal'
import Modal from '../ui/Modal'
import AddAdmin from './AddAdmin'
import EditAdmin from './EditAdmin'

const PAGE_SIZE = 10

function AdminList() {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)
  const [modalAction, setModalAction] = useState(null) // { id, type: 'block' | 'unblock' }
  const [modalType, setModalType] = useState(null) // 'add' | 'edit' | null
  const [editAdminId, setEditAdminId] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [adminToDelete, setAdminToDelete] = useState(null)

  useEffect(() => {
    getAdmins()
    // eslint-disable-next-line
  }, [])

  const getAdmins = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}getalladmins`
      )
      if (response.status === 200) {
        setData(response.data)
      }
    } catch (error) {
      toast.error('Failed to fetch admins')
    }
    setIsLoading(false)
  }

  const deleteAdmin = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}delete_user/${id}`
      )
      toast.success(response.data)
      getAdmins()
    } catch (error) {
      toast.error('Failed to delete admin')
    }
  }

  const blockParent = async (id) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}block_user/${id}`
      )
      toast.success(response.data)
      getAdmins()
    } catch (error) {
      toast.error('Failed to block admin')
    }
  }

  const unblockParent = async (id) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}unblock_user/${id}`
      )
      toast.success(response.data)
      getAdmins()
    } catch (error) {
      toast.error('Failed to unblock admin')
    }
  }

  const handleSearch = (query) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  // Filtered and paginated data
  const filteredAdmins = data.filter(
    (admin) =>
      (
        admin.first_name +
        ' ' +
        (admin.middle_name || '') +
        ' ' +
        (admin.last_name || '')
      )
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchQuery.toLowerCase())
  )
  const totalPages = Math.ceil(filteredAdmins.length / PAGE_SIZE) || 1
  const paginatedAdmins = filteredAdmins.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page)
  }

  const handleStatusToggle = (id, currentStatus) => {
    setModalAction({ id, type: currentStatus ? 'block' : 'unblock' })
    setModalOpen(true)
  }

  const handleModalConfirm = async () => {
    if (modalAction) {
      if (modalAction.type === 'block') {
        await blockParent(modalAction.id)
      } else {
        await unblockParent(modalAction.id)
      }
    }
    setModalOpen(false)
    setModalAction(null)
  }

  const handleModalCancel = () => {
    setModalOpen(false)
    setModalAction(null)
  }

  const handleOpenAddModal = () => {
    setModalType('add')
  }

  const handleOpenEditModal = (adminId) => {
    setEditAdminId(adminId)
    setModalType('edit')
  }

  const handleCloseModal = () => {
    setModalType(null)
    setEditAdminId(null)
    getAdmins() // Refresh list after add/edit
  }

  const handleDeleteClick = (id) => {
    setAdminToDelete(id)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (adminToDelete) {
      await deleteAdmin(adminToDelete)
    }
    setDeleteModalOpen(false)
    setAdminToDelete(null)
  }

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false)
    setAdminToDelete(null)
  }

  return (
    <section className="bg-white border rounded-[10px] border-[#F0F0F0]">
      <div className="flex justify-between items-center border px-5 py-4 border-[#F0F0F0]">
        <h2 className="text-lg text-[#030303] font-bold">Admin List</h2>
        <div className="flex items-center gap-2.5">
          <SearchInput
            placeholder="Search admin..."
            onSearch={handleSearch}
            className="w-[376px] max-md:w-[300px] max-sm:w-[200px]"
          />
          <button
            className="flex items-center gap-1 bg-[#DE3C7D] text-white p-2.5 rounded-md hover:bg-[#c8356f] transition-colors"
            onClick={handleOpenAddModal}
          >
            <span className="text-xs">Add Admin</span>
            <AddIcon />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DE3C7D]"></div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="text-sm text-[#535862] font-medium text-center border w-[100px] px-5 py-3.5 border-[#F0F0F0]">
                  S.No
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0]">
                  Name
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-[280px] px-5 py-3.5 border-[#F0F0F0]">
                  Email Address
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-[220px] px-5 py-3.5 border-[#F0F0F0]">
                  Phone
                </th>
                <th className="text-sm text-[#535862] font-medium text-center border px-5 py-3.5 border-[#F0F0F0]">
                  Status
                </th>
                <th className="text-sm text-[#535862] font-medium text-center px-5 py-3.5">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedAdmins.map((admin, index) => (
                <tr key={admin._id} className="border border-[#F0F0F0]">
                  <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                    {String((currentPage - 1) * PAGE_SIZE + index + 1).padStart(
                      2,
                      '0'
                    )}
                  </td>
                  <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0] font-semibold">
                    {admin.first_name} {admin.middle_name} {admin.last_name}
                  </td>
                  <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                    {admin.email}
                  </td>
                  <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                    {admin.phone}
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                    <div className="flex justify-center">
                      <StatusToggle
                        initialStatus={admin.status}
                        onChange={() =>
                          handleStatusToggle(admin._id, admin.status)
                        }
                      />
                    </div>
                  </td>
                  <td className="px-5 py-2">
                    <ActionButtons
                      onEdit={() => handleOpenEditModal(admin._id)}
                      onDelete={() => handleDeleteClick(admin._id)}
                    />
                  </td>
                </tr>
              ))}
              {paginatedAdmins.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-[#535862]">
                    No admins found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <div className="flex justify-end items-center gap-2 px-5 py-4">
          <button
            className={`px-3 py-1 rounded-md border text-sm font-medium ${
              currentPage === 1
                ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                : 'text-[#DE3C7D] border-[#DE3C7D] hover:bg-[#FFF0F6]'
            }`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              className={`px-3 py-1 rounded-md border text-sm font-medium ${
                currentPage === idx + 1
                  ? 'bg-[#DE3C7D] text-white border-[#DE3C7D]'
                  : 'text-[#DE3C7D] border-[#DE3C7D] hover:bg-[#FFF0F6]'
              }`}
              onClick={() => handlePageChange(idx + 1)}
            >
              {idx + 1}
            </button>
          ))}
          <button
            className={`px-3 py-1 rounded-md border text-sm font-medium ${
              currentPage === totalPages
                ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                : 'text-[#DE3C7D] border-[#DE3C7D] hover:bg-[#FFF0F6]'
            }`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
      <ConfirmModal
        open={modalOpen}
        title={
          modalAction?.type === 'block'
            ? 'Deactivate Sub Admin'
            : 'Activate Sub Admin'
        }
        message={
          modalAction?.type === 'block'
            ? 'Are you sure you want to Deactivate Sub Admin?'
            : 'Are you sure you want to Activate Sub Admin?'
        }
        onCancel={handleModalCancel}
        onConfirm={handleModalConfirm}
      />
      <Modal open={modalType === 'add'} onClose={handleCloseModal}>
        <AddAdmin onClose={handleCloseModal} />
      </Modal>
      <Modal open={modalType === 'edit'} onClose={handleCloseModal}>
        <EditAdmin adminId={editAdminId} onClose={handleCloseModal} />
      </Modal>
      <ConfirmModal
        open={deleteModalOpen}
        title="Delete Admin"
        message="Are you sure you want to delete this admin? This action cannot be undone."
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </section>
  )
}

export default AdminList
